import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { baseShot, Shot } from '../stores/reducers/shots';
import { GreenroomState as GS } from '../stores/types/types';
import { LOCATION_CANVAS_ROUTES } from '../stores/reducers/video';
import { getOnsiteLayout, isOneUpLayout } from '../utils/video-layout';

const useCanvasConfig = (inputShot: Shot | undefined = baseShot) => {
	const onsiteLocationSession = useSelector((s: GS) => s.VideoReducer.onsiteLocationSession);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const limitedInput = useSelector((s: GS) => s.SessionReducer.limitedInput);
	const limitedInputSmartCanvas = useSelector((s: GS) => s.SessionReducer.limitedInputSmartCanvas);
	const user = useSelector((s: GS) => s.SessionReducer.user);

	const hideSelf = useMemo(
		() => (
			limitedInput
				? limitedInputSmartCanvas
				: onsiteLocations.find((l) => l.id === onsiteLocationSession?.id)?.hideSelfView ?? false
		),
		[
			onsiteLocationSession,
			onsiteLocations,
			limitedInput,
			limitedInputSmartCanvas,
		],
	);

	// only use remove onsite feed from canvas if both are true:
	// 1) on the onsite location session canvas view
	// 2) "Smart Canvas" setting is enabled for the current location
	const shouldGetOnsiteCanvasConfig = useMemo(
		() => !!onsiteLocationSession
			&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession?.route)
			&& hideSelf,
		[onsiteLocationSession, hideSelf],
	);

	const getCanvasFeedsAndLayout = (shot: Shot) => {
		const {
			shot_data: {
				shotFeeds,
				forcedLayout,
			},
		} = shot;

		// NOTE: handling one up layouts will be addressed in a separate ticket
		if (!shouldGetOnsiteCanvasConfig || isOneUpLayout(forcedLayout)) {
			return { shotFeeds, forcedLayout };
		}

		// remove onsite feed from shot feeds if on canvas
		const filteredFeeds = shotFeeds
			.filter((f) => (
				f.audioOnly
				|| (onsiteLocationSession?.id && f.onsiteLocationId !== onsiteLocationSession?.id)
				|| (!onsiteLocationSession?.id && f.presenter !== user?.gr_user)
			))
			.map((f, i) => ({ ...f, position: i }));

		// adjust layout for each removed feed
		const numRemoved = shotFeeds.length - filteredFeeds.length;
		let newLayout = forcedLayout;
		for (let i = 0; i < numRemoved; i += 1) {
			newLayout = getOnsiteLayout(newLayout);
		}

		return { shotFeeds: filteredFeeds, forcedLayout: newLayout };
	};

	const { shotFeeds, forcedLayout } = useMemo(
		() => getCanvasFeedsAndLayout(inputShot),
		[
			inputShot.shot_data.shotFeeds,
			inputShot.shot_data.forcedLayout,
			shouldGetOnsiteCanvasConfig,
		],
	);

	const showingHoldSlide = useMemo(
		() => (
			shouldGetOnsiteCanvasConfig // location canvas view with smart canvas enabled
			&& isOneUpLayout(forcedLayout) // shot feed is a one up
			&& shotFeeds.some((feed) => ( // on canvas shot feed is the onsite user
				!feed.audioOnly
				&& ((onsiteLocationSession?.id && feed.onsiteLocationId === onsiteLocationSession?.id)
				|| (!onsiteLocationSession?.id && feed.presenter === user?.gr_user))
			))
		),
		[
			shouldGetOnsiteCanvasConfig,
			forcedLayout,
			shotFeeds,
			onsiteLocationSession,
			user,
		],
	);

	return {
		shotFeeds,
		forcedLayout,
		showingHoldSlide,
		shouldGetOnsiteCanvasConfig,
	};
};

export default useCanvasConfig;
